import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Category } from '@/lib/api';
import { useUpdateCategory } from '@/hooks/useCategories';
import { BUDGET_TYPES, type BudgetTreatment, type BudgetType } from '@/lib/constants';
import {
  BUDGET_BUCKET_PLACEHOLDER,
  BUDGET_CATEGORY_TRANSACTIONS,
  BUDGET_CHOOSE_BUCKET_HINT,
  BUDGET_ROW_TOGGLE,
  BUDGET_UNASSIGNED_LABEL,
  BUDGET_UNSUPPORTED_LABEL,
  budgetAssignmentSummary,
  budgetBucketLabel,
  budgetCategoryTransactionsLabel,
  budgetSavedAnnouncement,
  budgetTreatmentGroupLabel,
  translateBudgetTreatment,
  translateBudgetType,
} from '@/lib/translations';
import { cn } from '@/lib/utils';
import {
  assignmentState,
  committedAssignment,
  resolveBucketPick,
  resolveTreatmentPick,
  treatmentRequiresType,
  type AssignmentPatch,
  type AssignmentPick,
} from '@/lib/utils/budget';
import { getCategoryColorHsl } from '@/lib/utils/categoryColor';
import { TreatmentPills } from './TreatmentPills';

export interface CategoryRowProps {
  category: Category;
  // Start with the choices open — on the pending filter, where every row is there to be classified.
  defaultExpanded?: boolean;
  // Called as a request is sent, so the page can keep the row in a filtered list: a save takes it out of
  // "sem classificação" the moment it lands, and undoing is picking something else.
  onSubmit?: (categoryId: string) => void;
  // Called with a screen-reader message once a save lands.
  onSaved?: (message: string) => void;
  // In a group view (arrived from an Orçamento row): the way into this category's transactions for that month.
  transactionsHref?: string;
}

// Declared at module level on purpose: inside the page component it was a new component type on every
// render, so React remounted every row — and this row owns state that can't survive that.
export const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  defaultExpanded = false,
  onSubmit,
  onSaved,
  transactionsHref,
}) => {
  // One mutation per row: each row has its own pending state, and saves on different rows can overlap.
  const update = useUpdateCategory();
  // A treatment picked before its bucket. Nothing is sent until the pair is legal, so it lives here; it is
  // tied to the pair it was picked on and lapses by itself once a save changes that pair.
  const [draft, setDraft] = useState<{ basedOn: string; treatment: BudgetTreatment } | null>(null);
  // Opened or closed by hand; until then the row follows defaultExpanded and any unfinished pick.
  const [open, setOpen] = useState<boolean | null>(null);
  const panelId = useId();

  const colorHsl = getCategoryColorHsl(category.id, category.type);
  const unsupported = assignmentState(category) === 'unsupported';
  const committed = committedAssignment(category);
  const committedKey = `${category.budget_treatment ?? ''}|${category.budget_type ?? ''}`;
  const activeDraft = draft?.basedOn === committedKey ? draft : null;

  // What the row shows: the request in flight, else an unfinished pick, else what the server has. Showing
  // the request while it flies — rather than clearing local state when it settles — means the row never
  // flashes back to the old pair between the response landing and the list re-rendering.
  const inFlight = update.isPending ? update.variables : undefined;
  const shownTreatment: AssignmentPick = inFlight
    ? inFlight.budget_treatment || 'unassigned'
    : activeDraft?.treatment ?? committed.treatment ?? 'unassigned';
  const shownBucket: BudgetType | '' = inFlight ? inFlight.budget_type ?? '' : activeDraft ? '' : committed.bucket ?? '';

  const needsBucket = shownTreatment !== 'unassigned' && treatmentRequiresType(shownTreatment);
  const awaitingBucket = needsBucket && !shownBucket;

  const meta = unsupported
    ? BUDGET_UNSUPPORTED_LABEL
    : shownTreatment === 'unassigned'
    ? BUDGET_UNASSIGNED_LABEL
    : awaitingBucket
    ? `${translateBudgetTreatment(shownTreatment)} · ${BUDGET_CHOOSE_BUCKET_HINT}`
    : budgetAssignmentSummary(shownTreatment, shownBucket);

  const send = (patch: AssignmentPatch) => {
    onSubmit?.(category.id);
    update.mutate(
      { id: category.id, ...patch },
      {
        onSuccess: (saved) => {
          const { treatment, bucket } = committedAssignment(saved);
          const summary = treatment ? budgetAssignmentSummary(treatment, bucket) : BUDGET_UNASSIGNED_LABEL;
          onSaved?.(budgetSavedAnnouncement(saved.name, summary));
        },
      }
    );
  };

  // While a save is in flight further picks are ignored rather than the controls disabled: a focused control
  // that becomes disabled drops keyboard focus, and the row would lose the user's place after every pick.
  const handleTreatmentPick = (pick: AssignmentPick) => {
    if (update.isPending || pick === shownTreatment) return;

    // Nothing to clear: drop an unfinished pick and stay unassigned.
    if (pick === 'unassigned' && assignmentState(category) === 'unassigned') {
      setDraft(null);
      return;
    }

    const result = resolveTreatmentPick(pick, shownBucket);
    if (result.kind === 'needs-bucket') {
      setDraft({ basedOn: committedKey, treatment: result.treatment });
      return;
    }

    setDraft(null);
    send(result.patch);
  };

  const handleBucketPick = (value: string) => {
    if (update.isPending || shownTreatment === 'unassigned' || !treatmentRequiresType(shownTreatment)) return;
    send(resolveBucketPick(shownTreatment, value as BudgetType));
  };

  // The row reads as its summary; the choices open in place. An unfinished pick holds it open (the bucket is
  // still to choose), and so does the pending filter, where every row is there to be classified.
  const expanded = !unsupported && (open ?? (defaultExpanded || awaitingBucket));
  const toggleLabel = expanded
    ? BUDGET_ROW_TOGGLE.close
    : shownTreatment === 'unassigned'
    ? BUDGET_ROW_TOGGLE.classify
    : BUDGET_ROW_TOGGLE.edit;

  return (
    <li
      aria-busy={update.isPending}
      className="px-2 py-2 rounded-md transition-colors animate-fade-in hover:bg-[hsl(var(--cat)/0.08)]"
      style={{ ['--cat' as string]: colorHsl }}
    >
      {/* Wraps instead of squeezing: the name keeps at least 10rem, so on a phone a second action (Transações, in
          the group view) drops to its own line rather than truncating the name to nothing. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3">
        <div className="flex min-w-0 flex-1 basis-40 items-center gap-2.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
            style={{ backgroundColor: `hsl(${colorHsl})` }}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="text-sm text-foreground truncate">{category.name}</p>
            <p className="text-xs text-muted-foreground truncate">{meta}</p>
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />}
          {transactionsHref && (
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="h-11 gap-1 text-muted-foreground hover:bg-muted hover:text-foreground sm:h-9"
            >
              <Link to={transactionsHref} aria-label={budgetCategoryTransactionsLabel(category.name)}>
                {BUDGET_CATEGORY_TRANSACTIONS}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </Button>
          )}
          {!unsupported && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-expanded={expanded}
              aria-controls={panelId}
              // The visible word is short; the name says whose choices these are.
              aria-label={`${toggleLabel} — ${category.name}`}
              onClick={() => setOpen(!expanded)}
              className="h-11 text-muted-foreground hover:bg-muted hover:text-foreground sm:h-9"
            >
              {toggleLabel}
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div
          id={panelId}
          className={cn(
            'mt-1 mb-1 flex flex-col gap-2 pl-5 transition-opacity sm:flex-row sm:flex-wrap sm:items-center',
            update.isPending && 'opacity-70'
          )}
        >
          <TreatmentPills
            categoryType={category.type}
            value={shownTreatment}
            onPick={handleTreatmentPick}
            label={budgetTreatmentGroupLabel(category.name)}
          />
          {/* Only where a group applies: Renda and Fora do orçamento carry none, and a disabled "Sem grupo" on
              every such row was noise. */}
          {needsBucket && (
            <Select value={shownBucket} onValueChange={handleBucketPick}>
              <SelectTrigger
                aria-label={budgetBucketLabel(category.name)}
                className={cn('h-11 w-full text-sm sm:h-9 sm:w-48', awaitingBucket && 'border-foreground/50')}
              >
                <SelectValue placeholder={BUDGET_BUCKET_PLACEHOLDER} />
              </SelectTrigger>
              <SelectContent>
                {BUDGET_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {translateBudgetType(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </li>
  );
};
